import React, { useRef, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import './SwipeableListItem.css';

const MAX_SWIPE = 64;
const SNAP_THRESHOLD = 40;
const SWIPE_DEAD_ZONE = 5;

// Module-level: only one swipe can be open at a time.
// When a new pointerDown happens anywhere, close the previous one first.
let closeCurrentSwipe = null;

export default function SwipeableListItem({ children, onDelete, className = '', disableSwipe = false, onSwipeStateChange, ...props }) {
  const contentRef = useRef(null);
  const deleteRef = useRef(null);
  const startXRef = useRef(null);
  const offsetRef = useRef(0);
  const draggingRef = useRef(false);
  const didSwipeRef = useRef(false);
  const isOpenRef = useRef(false);
  const itemRef = useRef(null);
  const rafRef = useRef(null);
  const pointerIdRef = useRef(null);

  const applyTransform = useCallback((offset) => {
    if (contentRef.current) {
      contentRef.current.style.transform = `translate3d(${offset}px, 0, 0)`;
    }
    if (deleteRef.current) {
      const r = offset < 0 ? (-offset - MAX_SWIPE) : -MAX_SWIPE;
      deleteRef.current.style.right = `${r}px`;
    }
    if (itemRef.current) {
      if (offset < 0) {
        itemRef.current.classList.add('open');
      } else {
        itemRef.current.classList.remove('open');
      }
    }
  }, []);

  // Animate closed and reset all state
  const closeSwipe = useCallback(() => {
    if (!isOpenRef.current && offsetRef.current === 0) return;
    isOpenRef.current = false;
    offsetRef.current = 0;
    draggingRef.current = false;
    startXRef.current = null;
    didSwipeRef.current = false;
    if (contentRef.current) {
      contentRef.current.style.transition = '';
    }
    if (deleteRef.current) {
      deleteRef.current.style.transition = '';
    }
    applyTransform(0);
    if (onSwipeStateChange) onSwipeStateChange(false);
  }, [applyTransform, onSwipeStateChange]);

  // Unregister this instance from the global slot on unmount
  useEffect(() => {
    return () => {
      if (closeCurrentSwipe === closeSwipe) {
        closeCurrentSwipe = null;
      }
    };
  }, [closeSwipe]);

  // Reset when swipe gets disabled (e.g. during drag-reorder)
  useEffect(() => {
    if (disableSwipe) {
      startXRef.current = null;
      draggingRef.current = false;
      didSwipeRef.current = false;
      offsetRef.current = 0;
      isOpenRef.current = false;
      pointerIdRef.current = null;
      if (contentRef.current) {
        contentRef.current.style.transition = '';
        contentRef.current.style.transform = 'translate3d(0, 0, 0)';
      }
      if (deleteRef.current) {
        deleteRef.current.style.transition = '';
        deleteRef.current.style.right = `-${MAX_SWIPE}px`;
      }
      if (itemRef.current) {
        itemRef.current.classList.remove('open');
      }
      if (closeCurrentSwipe === closeSwipe) {
        closeCurrentSwipe = null;
      }
      if (onSwipeStateChange) onSwipeStateChange(false);
    }
  }, [disableSwipe, onSwipeStateChange, closeSwipe]);

  const handlePointerDown = useCallback((e) => {
    // Always close any other open swipe first, even if this one is disabled
    if (closeCurrentSwipe && closeCurrentSwipe !== closeSwipe) {
      closeCurrentSwipe();
      closeCurrentSwipe = null;
    }

    if (disableSwipe) return;
    // Capture pointer so events keep firing even if finger leaves the element
    if (e.target && e.target.setPointerCapture) {
      try { e.target.setPointerCapture(e.pointerId); } catch (_) {}
    }
    pointerIdRef.current = e.pointerId;
    startXRef.current = e.clientX;
    draggingRef.current = true;
    didSwipeRef.current = false;

    // Remove transition during active drag for instant response
    if (contentRef.current) {
      contentRef.current.style.transition = 'none';
    }
    if (deleteRef.current) {
      deleteRef.current.style.transition = 'none';
    }
  }, [disableSwipe, closeSwipe]);

  const handlePointerMove = useCallback((e) => {
    if (disableSwipe || !draggingRef.current || startXRef.current === null) return;

    const delta = e.clientX - startXRef.current;

    // If the item was in "open" state, adjust delta relative to the open position
    const baseOffset = isOpenRef.current ? -MAX_SWIPE : 0;
    let newOffset = baseOffset + delta;

    // Clamp
    newOffset = Math.max(newOffset, -MAX_SWIPE);
    newOffset = Math.min(newOffset, 0);

    offsetRef.current = newOffset;

    // Use rAF for smooth rendering
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      applyTransform(newOffset);
    });

    // Mark that a real swipe happened (past dead zone)
    if (Math.abs(delta) > SWIPE_DEAD_ZONE) {
      if (!didSwipeRef.current) {
        didSwipeRef.current = true;
        if (onSwipeStateChange) onSwipeStateChange(true);
      }
    }
  }, [disableSwipe, applyTransform, onSwipeStateChange]);

  const handlePointerUp = useCallback((e) => {
    if (disableSwipe || !draggingRef.current) return;
    draggingRef.current = false;
    startXRef.current = null;

    // Release pointer capture
    if (e && e.target && e.target.releasePointerCapture && pointerIdRef.current !== null) {
      try { e.target.releasePointerCapture(pointerIdRef.current); } catch (_) {}
    }
    pointerIdRef.current = null;

    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    // Re-enable transitions for snap animation
    if (contentRef.current) {
      contentRef.current.style.transition = '';
    }
    if (deleteRef.current) {
      deleteRef.current.style.transition = '';
    }

    // Snap to open or closed
    const shouldOpen = offsetRef.current <= -SNAP_THRESHOLD;
    const finalOffset = shouldOpen ? -MAX_SWIPE : 0;
    offsetRef.current = finalOffset;
    isOpenRef.current = shouldOpen;
    applyTransform(finalOffset);

    if (shouldOpen) {
      // Register this instance as the globally open swipe
      closeCurrentSwipe = closeSwipe;
    } else {
      // Clear global slot if it was us
      if (closeCurrentSwipe === closeSwipe) {
        closeCurrentSwipe = null;
      }
    }

    // Delay clearing swipe state so click events are blocked
    if (onSwipeStateChange) {
      if (!shouldOpen && didSwipeRef.current) {
        setTimeout(() => onSwipeStateChange(false), 80);
      } else if (!shouldOpen && !didSwipeRef.current) {
        onSwipeStateChange(false);
      }
      // If opened, keep swiping=true so clicks stay blocked
    }
    didSwipeRef.current = false;
  }, [disableSwipe, applyTransform, onSwipeStateChange, closeSwipe]);

  // Close swipe when clicking anywhere outside this item
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (isOpenRef.current && itemRef.current && !itemRef.current.contains(e.target)) {
        closeSwipe();
        if (closeCurrentSwipe === closeSwipe) {
          closeCurrentSwipe = null;
        }
      }
    };
    document.addEventListener('pointerdown', handleOutsideClick, true);
    return () => document.removeEventListener('pointerdown', handleOutsideClick, true);
  }, [closeSwipe]);

  const handleDeleteClick = useCallback(() => {
    if (onDelete) onDelete();
    isOpenRef.current = false;
    offsetRef.current = 0;
    applyTransform(0);
    if (closeCurrentSwipe === closeSwipe) {
      closeCurrentSwipe = null;
    }
    if (onSwipeStateChange) onSwipeStateChange(false);
  }, [onDelete, applyTransform, onSwipeStateChange, closeSwipe]);

  return (
    <motion.div className={className} {...props} style={{ ...props.style, width: '100%' }}>
      <div className="swipeable-item" ref={itemRef}>
        <div
          className="delete-icon"
          ref={deleteRef}
          style={{ right: `-${MAX_SWIPE}px` }}
          onClick={handleDeleteClick}
        >
          🗑️
        </div>
        <div
          className="swipe-content"
          ref={contentRef}
          style={{
            transform: 'translate3d(0, 0, 0)',
            touchAction: disableSwipe ? 'none' : 'pan-y',
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <div className="task-row" style={{ width: '100%' }}>
            {children}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
